import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import * as api from '../lib/api';
import { useAuth } from './AuthProvider';
import { daysFromToday } from '../lib/dates';
import { isRecurring } from '../lib/constants';

const TicketsContext = createContext();

export const useTickets = () => useContext(TicketsContext);

const byName = (a, b) => a.name.localeCompare(b.name);

// Scoped counts for the header chips / status bar — computed from whatever set
// of tickets is currently visible (team or mine), so they always match the view.
function summarize(list) {
  const open = list.filter((t) => t.status !== 'done');
  let overdue = 0;
  let dueThisWeek = 0;
  let estimate = 0;
  for (const t of open) {
    estimate += t.estimate_hours || 0;
    const d = daysFromToday(t.due_date);
    if (d === null) continue;
    if (d < 0) overdue += 1;
    else if (d <= 7) dueThisWeek += 1;
  }
  return {
    total: list.length,
    open: open.length,
    overdue,
    due_this_week: dueThisWeek,
    estimate_hours: Math.round(estimate * 10) / 10,
    new: list.filter((t) => t.status === 'new').length,
  };
}

export function TicketsProvider({ children }) {
  const { user } = useAuth();
  const myId = user?.user_id ?? null;

  const [tickets, setTickets] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scope, setScope] = useState(() => localStorage.getItem('ticky-scope') || 'team');

  // Global create/edit modal state, so any view (and the header button) can open it.
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [defaultStatus, setDefaultStatus] = useState('new');

  // Mascot celebration: fires when a ticket newly reaches 'done' or 'validation'.
  // `key` bumps on every trigger so the overlay re-mounts and replays.
  const [celebration, setCelebration] = useState(null);
  const ticketsRef = useRef(tickets);
  useEffect(() => { ticketsRef.current = tickets; }, [tickets]);
  const celebrate = useCallback((status) => {
    if (status !== 'done' && status !== 'validation') return;
    setCelebration((c) => ({ status, key: (c?.key || 0) + 1 }));
  }, []);

  useEffect(() => { localStorage.setItem('ticky-scope', scope); }, [scope]);

  const loadAll = useCallback(async () => {
    const [t, m] = await Promise.all([api.fetchTickets(), api.fetchMembers()]);
    setTickets(t);
    setMembers(m.sort(byName));
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await loadAll();
    } catch (e) {
      console.error('Failed to load data', e);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [loadAll]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Silently refetch (no loading flash) when the user returns to the tab.
  const silentRefresh = useCallback(async () => {
    try {
      await loadAll();
    } catch (e) {
      console.error('Background refresh failed', e);
    }
  }, [loadAll]);

  useEffect(() => {
    window.addEventListener('focus', silentRefresh);
    return () => window.removeEventListener('focus', silentRefresh);
  }, [silentRefresh]);

  // "team" = everyone's tickets; "mine" = assigned to the current user.
  const visibleTickets = useMemo(() => (
    scope === 'mine' && myId
      ? tickets.filter((t) => (t.assignees || []).some((a) => a.id === myId))
      : tickets
  ), [scope, myId, tickets]);

  // Header chips count top-level tickets only, so breaking one down into
  // subtasks doesn't inflate "open".
  const summary = useMemo(() => summarize(visibleTickets.filter((t) => !t.parent_id)), [visibleTickets]);

  // Subtask progress per parent id ({ total, done }), derived from the store
  // so it stays right as subtasks are ticked without refetching.
  const subtaskStats = useMemo(() => {
    const map = {};
    for (const t of tickets) {
      if (!t.parent_id) continue;
      const s = map[t.parent_id] || (map[t.parent_id] = { total: 0, done: 0 });
      s.total += 1;
      if (t.status === 'done') s.done += 1;
    }
    return map;
  }, [tickets]);

  // Completing a recurring ticket makes the backend create its next occurrence.
  // The response only carries the ticket we saved, so refetch (silently) to
  // pull the new card into the board / planning views.
  const pullNextOccurrence = useCallback((saved, prevStatus) => {
    if (saved?.status === 'done' && prevStatus !== 'done' && isRecurring(saved)) {
      loadAll().catch((e) => console.error('Refresh after recurring ticket failed', e));
    }
  }, [loadAll]);

  const saveTicket = useCallback(async (payload, id) => {
    const prevStatus = id ? ticketsRef.current.find((t) => t.id === id)?.status : null;
    const saved = id ? await api.updateTicket(id, payload) : await api.createTicket(payload);
    setTickets((prev) => (
      prev.some((t) => t.id === saved.id)
        ? prev.map((t) => (t.id === saved.id ? saved : t))
        : [...prev, saved]
    ));
    // Celebrate only on a real transition into done/validation (not on create).
    if (id && saved.status && saved.status !== prevStatus) celebrate(saved.status);
    pullNextOccurrence(saved, prevStatus);
    return saved;
  }, [celebrate, pullNextOccurrence]);

  // Used by drag-and-drop on the board (status / position changes).
  const moveTicket = useCallback(async (id, changes) => {
    // Revert only the affected ticket on failure, so a concurrent move/edit on a
    // different card isn't clobbered by restoring a stale whole-array snapshot.
    const prevTicket = ticketsRef.current.find((t) => t.id === id);
    setTickets((prev) => prev.map((t) => (t.id === id ? { ...t, ...changes } : t)));
    if (changes.status && prevTicket && prevTicket.status !== changes.status) {
      celebrate(changes.status);
    }
    try {
      const saved = await api.updateTicket(id, changes);
      setTickets((prev) => prev.map((t) => (t.id === saved.id ? saved : t)));
      pullNextOccurrence(saved, prevTicket?.status);
    } catch (e) {
      console.error('Move failed, reverting', e);
      if (prevTicket) setTickets((prev) => prev.map((t) => (t.id === id ? prevTicket : t)));
    }
  }, [celebrate, pullNextOccurrence]);

  // Move a ticket to Validation with an optional note. Goes through a dedicated
  // endpoint (not moveTicket) so the note is saved as a comment and a Slack DM
  // goes out. Returns the updated ticket.
  const requestValidation = useCallback(async (id, payload) => {
    const prevStatus = ticketsRef.current.find((t) => t.id === id)?.status;
    const saved = await api.requestValidation(id, payload);
    setTickets((prev) => prev.map((t) => (t.id === saved.id ? saved : t)));
    if (saved.status === 'validation' && prevStatus !== 'validation') celebrate('validation');
    return saved;
  }, [celebrate]);

  // "Take it" — assign to the current user (backend uses request.user).
  const assignMe = useCallback(async (id) => {
    const saved = await api.assignTicketToMe(id);
    setTickets((prev) => prev.map((t) => (t.id === saved.id ? saved : t)));
    return saved;
  }, []);

  // Create an external requester user inline; add to the people list.
  const createExternalMember = useCallback(async (name, email) => {
    const u = await api.createExternalMember(name, email);
    setMembers((prev) => (prev.some((m) => m.id === u.id) ? prev : [...prev, u].sort(byName)));
    return u;
  }, []);

  const addAttachments = useCallback(async (id, files) => {
    const saved = await api.uploadAttachments(id, files);
    setTickets((prev) => prev.map((t) => (t.id === saved.id ? saved : t)));
    return saved;
  }, []);

  const removeAttachment = useCallback(async (ticketId, attId) => {
    await api.deleteAttachment(ticketId, attId);
    setTickets((prev) => prev.map((t) => (
      t.id === ticketId ? { ...t, attachments: (t.attachments || []).filter((a) => a.id !== attId) } : t
    )));
  }, []);

  // Deleting a parent takes its subtasks with it (the backend cascades), so
  // drop both optimistically; on failure reload rather than reconstruct.
  const removeTicket = useCallback(async (id) => {
    setTickets((prev) => prev.filter((t) => t.id !== id && t.parent_id !== id));
    try {
      await api.deleteTicket(id);
    } catch (e) {
      console.error('Delete failed, reloading', e);
      await loadAll().catch(() => {});
      throw e;
    }
  }, [loadAll]);

  const openNewTicket = useCallback((status = 'new') => {
    setEditing(null);
    setDefaultStatus(status);
    setModalOpen(true);
  }, []);

  const openEditTicket = useCallback((ticket) => {
    setEditing(ticket);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditing(null);
  }, []);

  const value = {
    tickets,
    visibleTickets,
    members,
    summary,
    subtaskStats,
    scope,
    setScope,
    celebration,
    myId,
    loading,
    error,
    reload,
    refresh: silentRefresh,
    saveTicket,
    moveTicket,
    requestValidation,
    assignMe,
    createExternalMember,
    addAttachments,
    removeAttachment,
    removeTicket,
    modalOpen,
    editing,
    defaultStatus,
    openNewTicket,
    openEditTicket,
    closeModal,
  };

  return <TicketsContext.Provider value={value}>{children}</TicketsContext.Provider>;
}

export default TicketsProvider;
